import { moduleApi } from "@amtana/module";
import type { EraseStep } from "@amtana/module";

import type {
  AccessRequest,
  AccessRequestsOutput,
  ApproveAccessRequestInput,
  DeclineAccessRequestInput,
  PreviewOrganizationInput,
  PreviewOrganizationOutput,
  RequestAccessInput,
} from "./access-requests.schemas.ts";

// The promise this module makes to everyone else: asking to join an organization
// without an invite, and an admin's decision on it (S3.08). It reaches organization
// (to look an organization up, check an existing membership and add the approved
// member) and notification (to tell admins and the requester) only through their
// own contracts, never by importing their tables.

export interface AccessRequestsApi extends EraseStep {
  /** A shared link's landing screen (S3.08): safe for any signed-in caller, whether
   * or not they end up asking. */
  previewOrganization(
    input: PreviewOrganizationInput & { readonly userId: string },
  ): Promise<PreviewOrganizationOutput>;
  /** Asks to join an organization without an invite (S3.08); refused outright for
   * someone already a member, for a duplicate pending request, for a cool-down
   * after a recent decline, and rate limited across every organization. Notifies
   * every admin of the organization. */
  requestAccess(input: RequestAccessInput & { readonly userId: string }): Promise<AccessRequest>;
  /** Every pending request of the caller's organization (S3.08). */
  accessRequests(input: { readonly organizationId: string }): Promise<AccessRequestsOutput>;
  /** Approves a request with a role, adding the requester as a member and
   * notifying them (S3.08). */
  approveAccessRequest(
    input: ApproveAccessRequestInput & { readonly actorUserId: string },
  ): Promise<AccessRequest>;
  /** Declines a request, starting its cool-down, and notifies the requester
   * (S3.08); leaves no membership behind. */
  declineAccessRequest(
    input: DeclineAccessRequestInput & { readonly actorUserId: string },
  ): Promise<AccessRequest>;
}

export const AccessRequestsApi = moduleApi<AccessRequestsApi>()("access-requests");
